export type AiServiceAccess = {
  accessToken: string;
  expiresAt: string;
  apiBaseUrl: string;
};

export type AiServiceIdentity = {
  userUuid: string;
  workspaceUuid: string;
};

/** A browser-only, memory-only credential scope. Disposing aborts all its requests. */
export class AiServiceSession {
  readonly controller = new AbortController();
  private access: AiServiceAccess | null = null;
  private pending: Promise<AiServiceAccess> | null = null;

  constructor(
    readonly identity: AiServiceIdentity,
    private readonly issue: (signal: AbortSignal) => Promise<AiServiceAccess>,
  ) {}

  dispose(): void {
    this.access = null;
    this.controller.abort();
  }

  invalidate(accessToken: string): void {
    if (this.access?.accessToken === accessToken) this.access = null;
  }

  async getAccess(): Promise<AiServiceAccess> {
    this.controller.signal.throwIfAborted();
    if (this.access && Date.parse(this.access.expiresAt) > Date.now() + 30_000) {
      return this.access;
    }
    if (this.pending) return this.pending;

    this.pending = this.issue(this.controller.signal).then((access) => {
      this.controller.signal.throwIfAborted();
      // Prevent a cookie/session switch racing with issuance from crossing cache owners.
      const payload = access.accessToken.split(".")[1];
      const claims: unknown = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
      if (!claims || typeof claims !== "object"
        || !("sub" in claims) || claims.sub !== this.identity.userUuid
        || !("workspaceUuid" in claims) || claims.workspaceUuid !== this.identity.workspaceUuid
        || access.apiBaseUrl !== "/ai/v1"
        || !Number.isFinite(Date.parse(access.expiresAt))
        || Date.parse(access.expiresAt) <= Date.now()) {
        throw new Error("AI access does not match the current session.");
      }
      this.access = access;
      return access;
    }).finally(() => { this.pending = null; });
    return this.pending;
  }
}

let activeSession: AiServiceSession | null = null;

export function activateAiServiceSession(session: AiServiceSession): () => void {
  activeSession?.dispose();
  activeSession = session;
  return () => {
    session.dispose();
    if (activeSession === session) activeSession = null;
  };
}

export function clearAiServiceSession(): void {
  activeSession?.dispose();
  activeSession = null;
}

export function getAiServiceSession(url: string): AiServiceSession {
  const workspaceUuid = /^\/ai\/v1\/workspaces\/([^/?]+)(?:\/|$)/.exec(url)?.[1];
  if (!activeSession || workspaceUuid !== activeSession.identity.workspaceUuid) {
    throw new DOMException("AI session changed.", "AbortError");
  }
  activeSession.controller.signal.throwIfAborted();
  return activeSession;
}
